import { useState, useEffect } from 'react'
import { getDatosPersonales } from '@/common/utils/services/usuarios.service'
import { getIdUsuario } from '@/auth/auth.utils'

// Mapeo de IDs de tipo de teléfono del backend a descripciones del frontend
// Basado en los valores típicos de la base de datos
// IMPORTANTE: Debe coincidir con las descripciones en TIPOS_TELEFONO de formConstants.js
const TIPO_TELEFONO_MAP = {
  1: 'Fijo',
  2: 'Fijo',
  3: 'Fijo',
  4: 'Fijo',
  5: 'Fijo',
  6: 'Celular',
  7: 'Celular',
  8: 'Celular',
}

/**
 * Hook para obtener y gestionar los datos personales del usuario
 * @returns {Object} Estado y funciones para los datos personales
 */
export const useDatosPersonales = () => {
  const [datos, setDatos] = useState({
    apellido: '',
    nombre: '',
    correoElectronico: '',
    tipoTelefonoPrincipal: '',
    numeroTelefonoPrincipal: '',
    tipoTelefonoAlternativo: '',
    numeroTelefonoAlternativo: '',
    // IDs numéricos crudos del backend (para payloads de otros endpoints)
    tipoTelefonoPrincipalId: null,
    tipoTelefonoAlternativoId: null,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  // Indica si el fetch inicial de datos personales ya terminó (con o sin datos).
  // Permite a los consumidores inicializar formularios recién cuando se conoce
  // el estado final de los datos, evitando inicializaciones prematuras con vacíos.
  const [hasFetched, setHasFetched] = useState(false)

  useEffect(() => {
    const fetchDatos = async () => {
      const idUsuario = getIdUsuario()
      if (!idUsuario) {
        setError('No se encontró el ID de usuario')
        setHasFetched(true)
        return
      }

      setIsLoading(true)
      setError(null)

      const result = await getDatosPersonales(Number(idUsuario))

      if (result.code === 200 && result.data) {
        const data = result.data

        // Si hay teléfono pero no hay tipo, asumimos Celular como valor por defecto
        let tipoTelPrincipal = TIPO_TELEFONO_MAP[data.tipoTelefono] || ''
        if (!tipoTelPrincipal && data.telefono) {
          tipoTelPrincipal = 'Celular'
        }

        let tipoTelAlternativo = TIPO_TELEFONO_MAP[data.tipoTelefonoAlt] || ''
        if (!tipoTelAlternativo && data.telefonoAlt) {
          tipoTelAlternativo = 'Celular'
        }

        setDatos({
          apellido: data.apellido || '',
          nombre: data.nombre || '',
          correoElectronico: data.correoElectronico || '',
          tipoTelefonoPrincipal: tipoTelPrincipal,
          numeroTelefonoPrincipal: data.telefono || '',
          tipoTelefonoAlternativo: tipoTelAlternativo,
          numeroTelefonoAlternativo: data.telefonoAlt || '',
          tipoTelefonoPrincipalId: data.tipoTelefono ?? null,
          tipoTelefonoAlternativoId: data.tipoTelefonoAlt ?? null,
        })
      } else {
        setError(result.message || 'Error al cargar los datos personales')
      }

      setIsLoading(false)
      setHasFetched(true)
    }

    fetchDatos()
  }, [])

  const updateDatos = (newDatos) => {
    setDatos((prev) => ({ ...prev, ...newDatos }))
  }

  return {
    ...datos,
    isLoading,
    hasFetched,
    error,
    updateDatos,
  }
}
